import { useState } from "react";
import { toast } from "sonner";
import { X, Upload, Ruler, Camera } from "lucide-react";
import api from "@/lib/api";

const IDS = {
  editSheet: "edit-profile-sheet",
  closeBtn: "edit-profile-close",
  saveBtn: "edit-profile-save-btn",
  photoTab: "edit-profile-tab-photo",
  manualTab: "edit-profile-tab-manual",
  photoInput: "edit-profile-photo-input",
  nameInput: "edit-profile-name",
  ageInput: "edit-profile-age",
  cityInput: "edit-profile-city",
  height: "edit-profile-height",
  weight: "edit-profile-weight",
  shoulder: "edit-profile-shoulder",
  waist: "edit-profile-waist",
  hip: "edit-profile-hip",
};

const successMsg = (bodyTypeChanged, newBodyType) => {
  const base = "Got it! Your style profile is updated — suggestions will now match your new measurements.";
  if (bodyTypeChanged && newBodyType) {
    return `${base} We've updated your silhouette to ${newBodyType} — a beautiful shape to style.`;
  }
  return base;
};

const Field = ({ label, testId, value, onChange, type = "text", placeholder = "" }) => (
  <label className="flex flex-col gap-2">
    <span className="text-[10px] font-[Manrope] font-bold tracking-[0.2em] uppercase text-stone-500">{label}</span>
    <input
      data-testid={testId}
      type={type}
      value={value ?? ""}
      onChange={onChange}
      placeholder={placeholder}
      className="input"
    />
  </label>
);

export default function EditProfileSheet({ profile, onClose, onUpdated }) {
  const [mode, setMode] = useState(profile?.body_photo_path ? "photo" : "manual");
  const [saving, setSaving] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [form, setForm] = useState({
    name: profile?.name || "",
    age: profile?.age || "",
    city: profile?.city || "",
    height_cm: profile?.height_cm || "",
    weight_kg: profile?.weight_kg || "",
    shoulder_cm: profile?.shoulder_cm || "",
    waist_cm: profile?.waist_cm || "",
    hip_cm: profile?.hip_cm || "",
  });

  const buildBasicsPayload = () => {
    const p = {};
    if (form.name.trim() !== (profile?.name || "")) p.name = form.name.trim() || null;
    if (String(form.age) !== String(profile?.age || "")) p.age = form.age ? Number(form.age) : null;
    if (form.city.trim() !== (profile?.city || "")) p.city = form.city.trim() || null;
    return p;
  };

  const buildMeasurementsPayload = () => {
    const keys = ["height_cm", "weight_kg", "shoulder_cm", "waist_cm", "hip_cm"];
    const p = {};
    for (const k of keys) {
      const next = form[k] === "" || form[k] === null || form[k] === undefined ? null : Number(form[k]);
      const prev = profile?.[k] ?? null;
      if (next !== prev) p[k] = next;
    }
    return p;
  };

  const save = async () => {
    setSaving(true);
    try {
      let bodyTypeChanged = false;
      let newBodyType = null;

      // 1) Basics + measurements (single PUT)
      const putPayload = { ...buildBasicsPayload() };
      if (mode === "manual") Object.assign(putPayload, buildMeasurementsPayload());
      if (Object.keys(putPayload).length > 0) {
        const { data } = await api.put("/profile", putPayload);
        bodyTypeChanged = bodyTypeChanged || !!data.body_type_changed;
        if (data.body_type_changed) newBodyType = data.body_type;
      }

      // 2) Body photo (separate multipart) — replaces photo + re-derives body type
      if (mode === "photo" && photoFile) {
        const fd = new FormData();
        fd.append("file", photoFile);
        const { data } = await api.post("/profile/body-photo", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        bodyTypeChanged = bodyTypeChanged || !!data.body_type_changed;
        if (data.body_type_changed) newBodyType = data.body_type;
      }

      toast.success(successMsg(bodyTypeChanged, newBodyType), { duration: 5500 });
      onUpdated?.();
      onClose();
    } catch (err) {
      console.error("profile update failed", err);
      toast.error("Could not save profile. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid={IDS.editSheet}
      className="fixed inset-0 bg-stone-900/40 backdrop-blur-sm z-50 flex items-end justify-center"
    >
      <div className="w-full max-w-md bg-[#FBF9F6] rounded-t-3xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 pt-5 pb-3">
          <div className="font-[Cormorant_Garamond] text-2xl italic text-stone-900">Edit profile</div>
          <button
            data-testid={IDS.closeBtn}
            onClick={onClose}
            className="h-9 w-9 rounded-full border border-stone-200 flex items-center justify-center"
          >
            <X size={16} />
          </button>
        </div>

        <div className="overflow-y-auto px-6 pb-6 flex flex-col gap-5">
          <div>
            <div className="text-[10px] font-[Manrope] font-bold tracking-[0.25em] uppercase text-[#6B7A54] mb-2">Basics</div>
            <div className="grid grid-cols-1 gap-3">
              <Field label="Name" testId={IDS.nameInput} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              <div className="grid grid-cols-2 gap-3">
                <Field label="Age" testId={IDS.ageInput} type="number" value={form.age} onChange={(e) => setForm({ ...form, age: e.target.value })} />
                <Field label="City" testId={IDS.cityInput} value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="e.g., Mumbai" />
              </div>
            </div>
          </div>

          <div>
            <div className="text-[10px] font-[Manrope] font-bold tracking-[0.25em] uppercase text-[#6B7A54] mb-2">Silhouette</div>
            <div className="flex gap-2 p-1 bg-stone-100 rounded-full self-start mb-4 w-fit">
              <button
                data-testid={IDS.photoTab}
                onClick={() => setMode("photo")}
                className={`px-4 py-2 rounded-full text-sm font-[Manrope] flex items-center gap-2 ${mode === "photo" ? "bg-white shadow-sm text-stone-900" : "text-stone-500"}`}
              >
                <Camera size={14} strokeWidth={1.5} /> Photo
              </button>
              <button
                data-testid={IDS.manualTab}
                onClick={() => setMode("manual")}
                className={`px-4 py-2 rounded-full text-sm font-[Manrope] flex items-center gap-2 ${mode === "manual" ? "bg-white shadow-sm text-stone-900" : "text-stone-500"}`}
              >
                <Ruler size={14} strokeWidth={1.5} /> Manual
              </button>
            </div>

            {mode === "photo" ? (
              <label className="border-2 border-dashed border-stone-300 rounded-2xl p-6 flex flex-col items-center gap-2 cursor-pointer bg-white hover:bg-stone-50 transition-colors">
                <Upload size={18} strokeWidth={1.5} className="text-[#6B7A54]" />
                <span className="font-[Manrope] text-sm text-stone-600 text-center">
                  {photoFile ? photoFile.name : "Upload a new full-body photo"}
                </span>
                <span className="font-[Manrope] text-xs text-stone-400">
                  This replaces your old photo & re-derives your silhouette.
                </span>
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  data-testid={IDS.photoInput}
                  onChange={(e) => setPhotoFile(e.target.files?.[0] || null)}
                />
              </label>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <Field label="Height (cm)" testId={IDS.height} type="number" value={form.height_cm} onChange={(e) => setForm({ ...form, height_cm: e.target.value })} />
                <Field label="Weight (kg)" testId={IDS.weight} type="number" value={form.weight_kg} onChange={(e) => setForm({ ...form, weight_kg: e.target.value })} />
                <Field label="Shoulder (cm)" testId={IDS.shoulder} type="number" value={form.shoulder_cm} onChange={(e) => setForm({ ...form, shoulder_cm: e.target.value })} />
                <Field label="Waist (cm)" testId={IDS.waist} type="number" value={form.waist_cm} onChange={(e) => setForm({ ...form, waist_cm: e.target.value })} />
                <Field label="Hip (cm)" testId={IDS.hip} type="number" value={form.hip_cm} onChange={(e) => setForm({ ...form, hip_cm: e.target.value })} />
              </div>
            )}
          </div>
        </div>

        <div className="border-t border-stone-200 px-6 py-4 bg-[#FBF9F6]">
          <button
            data-testid={IDS.saveBtn}
            onClick={save}
            disabled={saving}
            className="w-full bg-[#6B7A54] hover:bg-[#556141] text-white rounded-full py-4 font-[Manrope] font-medium tracking-wide disabled:opacity-60 active:scale-[0.98] transition-[transform,background-color]"
          >
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

export { IDS as EDIT_IDS };
